import { apiFetch } from '../../api/apiFetch';

// ---------------------------------------------------------------------------
// TIER-3 Phase 1 — Parts platform API client.
//
// Endpoint paths/shapes marked "judgment" below are the ones the design doc
// (docs/planning/tier3-parts-platform-design.md §7 Phase 1 row) leaves to the
// implementer; everything else (composite create, rev-bump, clone, ingest
// idempotency contract) matches the doc verbatim. All calls go through the
// shared apiFetch wrapper (AUTH-4) — never plain fetch.
//
// {error, code} envelope (AUTH-13 family, extended per doc:360):
//   409 {error, code:'CONFLICT'}          — dup (part_number, revision) or
//                                           dup feature name on a part
//   400 {error, code:'MISSING_CONTEXT',
//        details:[{index, missing:[...]}]} — per-point missing required
//                                           entry_config keys (backend/measurementRoutes.js
//                                           missingDetails; NOT a flat `missing` array —
//                                           callers must flatten `details[].missing`
//                                           themselves, see PartMeasurementEntry.js)
//   401/402                                — requireFeature('parts') gate
//   404 {error, code:'NOT_FOUND'}
// ---------------------------------------------------------------------------

function backendUrl() {
    return process.env.REACT_APP_BACKEND_URL || '';
}

const JSON_HEADERS = { 'Content-Type': 'application/json' };

async function parseJson(res) {
    return res.json().catch(() => ({}));
}

/** Mint a per-reading idempotency key (GuidedEntry: one per reading, never per session).
 * Mirrors apiFetch.js's own private genUuid fallback — that helper isn't exported. */
export function genEntryId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

// ---------------------------------------------------------------------------
// Parts CRUD
// ---------------------------------------------------------------------------

/** GET /api/parts — current-rev list, minimal search. (judgment: shape) */
export async function listParts({ search = '', limit = 50, offset = 0 } = {}) {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    params.set('limit', String(limit));
    params.set('offset', String(offset));
    const res = await apiFetch(`${backendUrl()}/api/parts?${params.toString()}`, { credentials: 'include' });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

/** GET /api/parts/:id — detail, embeds features (+ each feature's streams). (judgment: shape) */
export async function getPart(partId) {
    const res = await apiFetch(`${backendUrl()}/api/parts/${partId}`, { credentials: 'include' });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

/**
 * POST /api/parts — the composite 2-minute-start create (doc §5/§7): part +
 * first feature + auto default stream in ONE transaction/request. Conflict
 * key = (part_number, revision) -> 409 CONFLICT (a new revision is legal).
 */
export async function createPart({ partNumber, name, revision, feature }) {
    const body = {
        part_number: partNumber,
        name: name || undefined,
        revision: revision || undefined,
        feature: {
            name: feature?.name,
            usl: feature?.usl ?? null,
            lsl: feature?.lsl ?? null,
            nominal: feature?.nominal ?? null,
            unit: feature?.unit || null,
        },
    };
    const res = await apiFetch(`${backendUrl()}/api/parts`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

/**
 * POST /api/parts/:id/rev-bump (doc §2.2, §7) — copy-not-mutation: new parts
 * row, same part_number, distinct revision, COPIES family_id; fresh
 * features+streams copying chart_type/subgroup_size/subgroup_strategy/
 * entry_config/gage_id/process_label but never lock/monitoring state (Phase 2
 * columns don't exist yet). Never sets cloned_from_part_id.
 */
export async function revBumpPart(partId, revision) {
    const res = await apiFetch(`${backendUrl()}/api/parts/${partId}/rev-bump`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify({ revision }),
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

/**
 * POST /api/parts/:id/clone (doc §2.6) — mints a NEW part_number + fresh
 * family_id, stamps cloned_from_part_id/cloned_from_feature_id. `features` is
 * the server's REAL per-feature override shape (backend/partsRoutes.js clone
 * route, body.features): an array of {id: <source feature id>, ...overrides}
 * — e.g. `[{ id: sourceFeature.id, usl: 99.9 }]` — pinned by
 * backend/tests/partsRoutes.test.js "POST /api/parts/:id/clone". There is no
 * `specDeltas` shape on the server; that name never existed on the wire.
 *
 * CloneDialog-adjacent note: Phase-1 clone UI (PartDetail.js) is
 * exact-copy-then-edit — it calls clonePart() with no `features` override at
 * all, then the user edits specs on the resulting clone afterward via the
 * normal feature PATCH. Per-feature delta inputs at clone time are not part
 * of the Phase-1 UI; `features` exists on this helper for API-contract parity
 * only until/unless that UI is built.
 */
export async function clonePart(partId, { partNumber, name, revision, features } = {}) {
    const res = await apiFetch(`${backendUrl()}/api/parts/${partId}/clone`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify({
            part_number: partNumber,
            name: name || undefined,
            revision: revision || undefined,
            features: features || undefined,
        }),
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

/** POST /api/parts/:id/archive — soft-delete via archived_at (doc §8.1 risk 8). (judgment: verb) */
export async function archivePart(partId) {
    const res = await apiFetch(`${backendUrl()}/api/parts/${partId}/archive`, {
        method: 'POST',
        credentials: 'include',
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

// ---------------------------------------------------------------------------
// Feature CRUD
// ---------------------------------------------------------------------------

/**
 * POST /api/parts/:id/features — create a feature on an existing part. Used
 * both for a plain "add feature" and the duplicate-with-suffix flow (doc
 * §2.2 owner decision 2: same callout, different spec at a different process
 * point -> name prefix + unique suffix, e.g. "OD Ø25.4 — Op20" / "— Final").
 * uq_features_part_name -> 409 CONFLICT on a name collision.
 */
export async function createFeature(partId, { name, usl, lsl, nominal, unit }) {
    const res = await apiFetch(`${backendUrl()}/api/parts/${partId}/features`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify({
            name,
            usl: usl ?? null,
            lsl: lsl ?? null,
            nominal: nominal ?? null,
            unit: unit || null,
        }),
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

/**
 * POST /api/parts/:id/features/:featureId/archive — soft-delete a feature.
 * Real route needs BOTH ids (backend/partsRoutes.js router.post
 * ('/:id/features/:featureId/archive', ...)) — not a bare
 * /api/features/:id/archive path.
 */
export async function archiveFeature(partId, featureId) {
    const res = await apiFetch(`${backendUrl()}/api/parts/${partId}/features/${featureId}/archive`, {
        method: 'POST',
        credentials: 'include',
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

// ---------------------------------------------------------------------------
// Measurements — Inspection view reads + guided-entry ingest
// ---------------------------------------------------------------------------

/**
 * GET /api/features/:id/measurements?process=&limit= — last-N readings for
 * the Inspection view (plain GET, NOT the Phase-2 live?after= poll). Real
 * response shape (backend/measurementRoutes.js, pinned by
 * backend/tests/measurementRoutes.test.js "verdict annotation (conformance)"):
 *   { featureId, streamId, usl, lsl, pctInSpec: number|null,
 *     points: [{ id, value, attrValue, externalId, operatorLabel, gageId,
 *                batchLabel, disposition, source, measuredAt,
 *                conformance: 'in_spec'|'out_of_spec'|null }],
 *     pagination: { limit, offset } }
 * — camelCase fields, `points` (not `measurements`).
 */
export async function listMeasurements(featureId, { process, limit = 25 } = {}) {
    const params = new URLSearchParams();
    if (process !== undefined && process !== null) params.set('process', process);
    params.set('limit', String(limit));
    const res = await apiFetch(`${backendUrl()}/api/features/${featureId}/measurements?${params.toString()}`, {
        credentials: 'include',
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

/**
 * POST /api/features/:id/measurements?process= — the ingest endpoint (doc
 * §3.2/§7): resolves default stream or ?process=, one or more points capped
 * 500/request, each point may carry an externalId (idempotency key — GuidedEntry
 * mints a fresh UUID per reading via genEntryId(), never one per session).
 * entry_config-required-context is validated only for rows that would insert;
 * any insertable row missing a required key 400s the WHOLE request with
 * {error, code:'MISSING_CONTEXT', details:[{index, missing:[...]}]} — a
 * per-point list, not a flat `missing` array (see the file-level doc comment).
 * Real 201 response shape (backend/measurementRoutes.js; alarms/limits are
 * Phase 2, not present here):
 *   { featureId, streamId, requested, inserted, skipped,
 *     points: [{ index, inserted, id, externalId, value, attrValue,
 *                disposition, conformance: 'in_spec'|'out_of_spec'|null,
 *                measuredAt }] }
 */
export async function postMeasurements(featureId, { points, process } = {}) {
    const params = new URLSearchParams();
    if (process !== undefined && process !== null) params.set('process', process);
    const qs = params.toString();
    const res = await apiFetch(`${backendUrl()}/api/features/${featureId}/measurements${qs ? `?${qs}` : ''}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify({ points }),
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

// ---------------------------------------------------------------------------
// Windowed capability snapshot (doc §2.5/§7 — through runAnalysis, not a new
// inline copy; the manual action, NOT the Phase-2 nudge ladder that suggests it).
// ---------------------------------------------------------------------------

/**
 * POST /api/features/:id/snapshot — the real contract (be-core lane, landing
 * concurrently with this fix): body {process?, windowFromId?, windowToId?,
 * description?}, no `n` field. Omitting windowFromId/windowToId means
 * "last-N window by default" server-side; the Phase-1 "Run snapshot" button
 * doesn't offer window selection yet, so this helper only ever sends
 * `process`.
 */
export async function runCapabilitySnapshot(featureId, { process, windowFromId, windowToId, description } = {}) {
    const body = {};
    if (process !== undefined) body.process = process;
    if (windowFromId !== undefined) body.windowFromId = windowFromId;
    if (windowToId !== undefined) body.windowToId = windowToId;
    if (description !== undefined) body.description = description;
    const res = await apiFetch(`${backendUrl()}/api/features/${featureId}/snapshot`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify(body),
    });
    const data = await parseJson(res);
    return { ok: res.ok, status: res.status, data };
}

const partsApi = {
    genEntryId,
    listParts,
    getPart,
    createPart,
    revBumpPart,
    clonePart,
    archivePart,
    createFeature,
    archiveFeature,
    listMeasurements,
    postMeasurements,
    runCapabilitySnapshot,
};

export default partsApi;
